/**
 * Display clean-up for directory rows imported from DENUE.
 *
 * INEGI publishes names and addresses in SHOUTING CAPS with registry filler
 * ("OTRO(ESPECIFIQUE) 106, LAS MISIONES"). Rendered as-is, a city guide reads
 * like a government export rather than a place to find storage. This is a
 * DISPLAY transform only — the stored row, the slug and the facility key are
 * untouched, so nothing about identity or URLs moves.
 *
 * Deterministic on purpose (plain `toLowerCase`/`toUpperCase`, no `Intl`):
 * these strings render on SSR'd pages and must serialize identically on both
 * sides of hydration.
 */

/** DENUE placeholders that stand in for a missing street or name. */
const REGISTRY_FILLER_RE =
  /\b(?:OTRO\s*\(\s*ESPECIFIQUE\s*\)|NINGUNO\b|SIN NOMBRE\b)\s*,?/gi

/** Only re-case a string that is mostly capitals — a mixed-case name was
 *  typed by a person (often the operator) and is left exactly as written. */
const SHOUTY_UPPERCASE_RATIO = 0.8
const MIN_LETTERS_TO_JUDGE = 4

/** Lowercase connectors, except as the first word. "La"/"El"/"Los" stay
 *  capitalized: in place and brand names ("La Paz", "El Toreo") they are part
 *  of the proper noun far more often than not. */
const LOWERCASE_CONNECTORS = new Set([
  'de',
  'del',
  'y',
  'e',
  'en',
  'a',
  'al',
  'con',
  'por',
  'para'
])

/** Abbreviations that read wrong in title case. */
const KEEP_UPPERCASE = new Set([
  'CP',
  'SA',
  'CV',
  'RL',
  'II',
  'III',
  'IV',
  'BC',
  'BCS',
  'NL',
  'GTO',
  'CDMX',
  'QROO',
  'SLP',
  'USA',
  // Institutions and brands that appear in addresses ("frente al IMSS").
  'BBVA',
  'CFE',
  'IMSS',
  'IPN',
  'ISSSTE',
  'OXXO',
  'UNAM'
])

/** Street-type abbreviations with no vowel, which the acronym rule below
 *  would otherwise keep in capitals. */
const ABBREVIATION_CASE: Record<string, string> = { BLVD: 'Blvd' }

/** "5TA", "3ER", "2DA." — an ordinal reads as "5ta", not as an acronym. */
const ORDINAL_RE = /^(\d+)(\p{L}{1,3})(\.?)$/u

const VOWEL_RE = /[AEIOUÁÉÍÓÚÜ]/i
const LETTER_RE = /\p{L}/gu
const UPPER_LETTER_RE = /\p{Lu}/gu
const MAX_ACRONYM_LETTERS = 4

function isShouty(value: string): boolean {
  const letters = value.match(LETTER_RE)?.length ?? 0
  if (letters < MIN_LETTERS_TO_JUDGE) return false
  const upper = value.match(UPPER_LETTER_RE)?.length ?? 0
  return upper / letters >= SHOUTY_UPPERCASE_RATIO
}

function capitalize(segment: string): string {
  if (!segment) return segment
  return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase()
}

/** Title-case the letter runs inside one token, keeping punctuation where it
 *  is: "SELF-STORAGE" → "Self-Storage", "U.LOK" → "U.Lok". */
function recaseToken(token: string, isFirst: boolean): string {
  const ordinal = token.match(ORDINAL_RE)
  if (ordinal) return `${ordinal[1]}${ordinal[2].toLowerCase()}${ordinal[3]}`
  if (/\d/.test(token)) return token
  const bare = token.replace(/[^\p{L}]/gu, '')
  if (!bare) return token
  const upperBare = bare.toUpperCase()
  if (KEEP_UPPERCASE.has(upperBare)) return token.toUpperCase()
  const abbreviation = ABBREVIATION_CASE[upperBare]
  if (abbreviation) return token.replace(/\p{L}+/u, abbreviation)
  // "C.P.", "S.A." — dotted single letters stay as written.
  if (/^(?:\p{L}\.)+$/u.test(token)) return token.toUpperCase()
  const lower = token.toLowerCase()
  if (!isFirst && LOWERCASE_CONNECTORS.has(lower)) return lower
  // Vowel-less short tokens are acronyms ("ZN", "GDL"), not words.
  if (bare.length <= MAX_ACRONYM_LETTERS && !VOWEL_RE.test(bare)) {
    return token.toUpperCase()
  }
  return token.replace(/\p{L}+/gu, capitalize)
}

/**
 * Clean a directory name or address for display.
 *
 * - drops DENUE filler tokens and the empty commas they leave behind
 * - title-cases a mostly-uppercase string; leaves mixed case untouched
 */
export function prettifyDirectoryText(
  value: string | null | undefined
): string {
  const stripped = (value ?? '')
    .replace(REGISTRY_FILLER_RE, ' ')
    .replace(/\s+,/g, ',')
    .replace(/,\s*,/g, ',')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s,]+|[\s,]+$/g, '')
  if (!isShouty(stripped)) return stripped
  return stripped
    .split(' ')
    .map((token, i) => recaseToken(token, i === 0))
    .join(' ')
}
